/**
 * Recensioni — the landings side of the public review API
 * (spec docs/superpowers/specs/2026-10-02-recensioni-design.md §4).
 *
 * Every call is cross-origin to the backend at `backendBaseUrl`, exactly like
 * the shared contact form (`credentials: 'omit'`, configured once in
 * main.tsx), so no nginx proxy block is needed. The token travels in the
 * path. Nothing here throws: every network or parse failure comes back as
 * `{ ok: false, status: 0 }`, so callers can always degrade to "as today".
 */
import { apiUrl, getContactFormConfig } from '@shared/contact-form/config'

export type ReviewState = 'open' | 'submitted' | 'expired' | 'revoked'

export interface SubmittedReview {
  rating: number
  text: string
  display_name: string
  consent_publish: boolean
  /** Consent given AND not hidden by staff: what the site shows right now. */
  published: boolean
}

/**
 * `GET /api/landing/review/<token>/`. For `expired` and `revoked` the backend
 * blanks the personal fields (names, town, review, google): those views use
 * only `sede`.
 */
export interface ReviewRequestInfo {
  state: ReviewState
  sede: { name: string; privacy_url: string }
  first_name: string
  display_name: string
  town: string
  review: SubmittedReview | null
  google: boolean
}

/** Body of `POST /api/landing/review/<token>/`. `website` is the honeypot: always empty. */
export interface ReviewSubmission {
  rating: number
  text: string
  display_name?: string
  consent_publish: boolean
  website: string
}

/** One published review from `GET /api/landing/reviews/?sede_id=`. `date` is `YYYY-MM`. */
export interface PublicReview {
  id: number
  display_name: string
  town: string
  rating: number
  text: string
  date: string
}

export interface PublicReviewList {
  count: number
  average: number | null
  google_review_url: string
  /** Google Place ID for the live rating (googleRating.ts); '' when not configured. */
  google_place_id: string
  /** The sede's Google profile (https only); '' when not configured. */
  google_profile_url: string
  reviews: PublicReview[]
}

export type ApiResult<T> = { ok: true; status: number; data: T } | { ok: false; status: number; data?: unknown }

type FetchLike = typeof fetch

function reviewPath(token: string, suffix = ''): string {
  return `/api/landing/review/${encodeURIComponent(token)}/${suffix}`
}

async function call<T>(
  path: string,
  init: RequestInit,
  parse: (body: unknown) => T | null,
  fetchImpl: FetchLike,
): Promise<ApiResult<T>> {
  let res: Response
  try {
    res = await fetchImpl(apiUrl(path), {
      ...init,
      credentials: getContactFormConfig().credentials,
      headers: { Accept: 'application/json', ...(init.body ? { 'Content-Type': 'application/json' } : {}) },
    })
  } catch {
    return { ok: false, status: 0 }
  }
  let body: unknown = null
  try {
    body = await res.json()
  } catch {
    /* empty or non-JSON body */
  }
  if (!res.ok) return { ok: false, status: res.status, data: body }
  const data = parse(body)
  // A 2xx we cannot read is treated like a network failure: never guess.
  return data === null ? { ok: false, status: 0 } : { ok: true, status: res.status, data }
}

const isObj = (v: unknown): v is Record<string, unknown> => !!v && typeof v === 'object' && !Array.isArray(v)
const str = (v: unknown): string => (typeof v === 'string' ? v : '')
const STATES: readonly ReviewState[] = ['open', 'submitted', 'expired', 'revoked']

/**
 * Staff type these URLs into the admin, and they end up in an href: only an
 * absolute https URL (or, for the privacy link, a same-site path) gets
 * through; anything else (javascript:, data:, //host) becomes ''.
 */
export function safeUrl(v: unknown, allowPath = false): string {
  const s = str(v).trim()
  if (allowPath && /^\/(?!\/)/.test(s)) return s
  try {
    return new URL(s).protocol === 'https:' ? s : ''
  } catch {
    return ''
  }
}

export function parseReviewRequest(body: unknown): ReviewRequestInfo | null {
  if (!isObj(body) || !STATES.includes(body.state as ReviewState)) return null
  const sede = isObj(body.sede) ? body.sede : {}
  const r = isObj(body.review) ? body.review : null
  return {
    state: body.state as ReviewState,
    sede: { name: str(sede.name), privacy_url: safeUrl(sede.privacy_url, true) },
    first_name: str(body.first_name),
    display_name: str(body.display_name),
    town: str(body.town),
    review: r
      ? {
          rating: typeof r.rating === 'number' ? r.rating : 0,
          text: str(r.text),
          display_name: str(r.display_name),
          consent_publish: r.consent_publish === true,
          // Older backends do not send it: then consent is the best guess.
          published: typeof r.published === 'boolean' ? r.published : r.consent_publish === true,
        }
      : null,
    google: body.google === true,
  }
}

export function parsePublicReviews(body: unknown): PublicReviewList | null {
  if (!isObj(body) || !Array.isArray(body.reviews)) return null
  const reviews: PublicReview[] = body.reviews.filter(isObj).flatMap((r) => {
    const rating = typeof r.rating === 'number' ? Math.round(r.rating) : NaN
    if (!(rating >= 1 && rating <= 5)) return []
    return [{ id: typeof r.id === 'number' ? r.id : 0, display_name: str(r.display_name), town: str(r.town), rating, text: str(r.text), date: str(r.date) }]
  })
  return {
    count: typeof body.count === 'number' ? body.count : reviews.length,
    average: typeof body.average === 'number' ? body.average : null,
    google_review_url: safeUrl(body.google_review_url),
    // Goes into a URL path: Place IDs are URL-safe base64-like strings.
    google_place_id: /^[A-Za-z0-9_-]{1,300}$/.test(str(body.google_place_id)) ? str(body.google_place_id) : '',
    google_profile_url: safeUrl(body.google_profile_url),
    reviews,
  }
}

export function fetchReviewRequest(token: string, fetchImpl: FetchLike = fetch) {
  return call(reviewPath(token), { method: 'GET' }, parseReviewRequest, fetchImpl)
}

export function submitReview(token: string, body: ReviewSubmission, fetchImpl: FetchLike = fetch) {
  return call(
    reviewPath(token),
    { method: 'POST', body: JSON.stringify(body) },
    (b) => (isObj(b) ? { google: b.google === true } : { google: false }),
    fetchImpl,
  )
}

export function optOut(token: string, fetchImpl: FetchLike = fetch) {
  return call(reviewPath(token, 'opt-out/'), { method: 'POST', body: '{}' }, () => ({ ok: true as const }), fetchImpl)
}

/** Withdraws the publication consent of this request's review (idempotent; 409 when there is no review yet). */
export function withdrawConsent(token: string, fetchImpl: FetchLike = fetch) {
  return call(reviewPath(token, 'withdraw/'), { method: 'POST', body: '{}' }, () => ({ ok: true as const }), fetchImpl)
}

export function fetchPublicReviews(sedeId: number, fetchImpl: FetchLike = fetch) {
  return call(`/api/landing/reviews/?sede_id=${encodeURIComponent(String(sedeId))}`, { method: 'GET' }, parsePublicReviews, fetchImpl)
}

/** The tracked redirect to the sede's Google review form (opened as a top-level link, never fetched). */
export function googleRedirectUrl(token: string): string {
  return apiUrl(reviewPath(token, 'google/'))
}
